'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '~/app/_components/Button';
import { TextInput } from '~/app/_components/TextInput';
import { pagesPath } from '~/libs/$path';
import { useNotification } from '~/providers/Notification';
import { useCognito } from '~/providers/cognito';

import styles from './style.module.css';

type FormValues = {
  email: string;
};

const formValuesSchema: z.Schema<FormValues> = z.object({
  email: z.string().email('メールアドレスを入力してください'),
});

export default function Page() {
  const { cognito } = useCognito();
  const router = useRouter();
  const { pushNotification } = useNotification();

  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      email: '',
    },
    resolver: zodResolver(formValuesSchema),
    mode: 'onBlur',
  });

  const onSubmit = async (values: FormValues) => {
    try {
      await cognito.forgotPassword(values.email);
      router.push(
        pagesPath.reset_password_confirm.$url().pathname +
          '?email=' +
          encodeURIComponent(values.email),
      );
    } catch (error) {
      if (error instanceof Error && error.name === 'LimitExceededException') {
        pushNotification({
          type: 'error',
          message:
            '短期間に複数回のリクエストがありました。少し時間をおいてから再度お試しください。',
        });
        return;
      }

      pushNotification({
        type: 'error',
        message: 'パスワードの再設定に失敗しました',
      });
      console.error({
        message: 'パスワードの再設定に失敗しました',
        error,
      });
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.logo}>
        <Image src='/logo.svg' alt='logo' height={32} width={182} />
      </div>
      <h1 className={styles.title}>パスワードの再設定</h1>
      <p className={styles.description}>
        パスワードを再設定するためにメールアドレスを入力してください。
        <br />
        社員番号でご利用中の場合は、会社の管理者の方にご連絡ください。
      </p>
      <form className={styles.form}>
        <div className={styles.email}>
          <TextInput control={control} name='email' label='メールアドレス' />
        </div>
      </form>
      <div className={styles.button}>
        <Button size='L' type='submit' onClick={handleSubmit(onSubmit)}>
          次へ
        </Button>
        <Button
          size='L'
          variant='secondary'
          type='button'
          isAnchor
          href={pagesPath.login.$url().pathname}
        >
          ログインへ
        </Button>
      </div>
    </div>
  );
}
